import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Where the API redirects a failed SSO handshake.
 *
 * The handshake is a browser navigation, so a JSON error body would render as
 * raw text on a blank page. Each failure gets an explanation of what to
 * actually do, because "invalid token" tells a user nothing they can act on.
 */
const GUIDANCE: Record<string, string> = {
  SSO_TOKEN_EXPIRED:
    'Sign-in links are valid for about a minute. This one sat too long — usually because the page was left open or reopened from history. Start again from the portal.',
  SSO_TOKEN_REPLAYED:
    'Each sign-in link works exactly once, which is what stops a copied link being reused. Refreshing this page or using a bookmark will always land here. Start again from the portal.',
  SSO_INVALID_TOKEN:
    'The sign-in link could not be verified. If this keeps happening, the portal and this system may be out of step — contact your administrator.',
  SSO_UNKNOWN_ISSUER:
    'The sign-in link came from somewhere this system does not recognise. Report this to your administrator.',
  USER_SUSPENDED:
    'Your identity was verified, but query access has not been granted. Approval happens here, not in the portal, so it is a separate step.',
};

export function AuthErrorPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? 'INTERNAL';
  const message = params.get('message') ?? 'Sign-in failed.';
  const loginUrl = useQuery({ queryKey: ['login-url'], queryFn: api.auth.loginUrl });

  return (
    <div className="flex h-full items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md animate-slide-up panel rounded-xl p-7">
        <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-danger/10 text-danger">
          <AlertTriangle className="h-5 w-5" strokeWidth={1.75} />
        </div>

        <h1 className="text-base font-semibold text-ink">Could not sign you in</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-muted">{message}</p>

        {GUIDANCE[code] && (
          <p className="mt-4 rounded-md border border-edge bg-surface-sunken p-3 text-2xs leading-relaxed text-ink-muted">
            {GUIDANCE[code]}
          </p>
        )}

        <a className="btn btn-primary mt-6 w-full py-2 text-sm" href={loginUrl.data?.url ?? '/'}>
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
          Try again from the portal
        </a>

        <p className="mt-4 font-mono text-2xs text-ink-faint">{code}</p>
      </div>
    </div>
  );
}
